import { useDroppable } from '@dnd-kit/core';
import { ReactNode } from 'react';
import { playsContainingAll } from '../../../functions';
import { ISourcedCard } from '../../../Models/CardModels';
import { IVisibleGameState, IVisiblePlay } from '../../../Models/StateModels';
import BurntCards from '../BurntCards/BurntCards';
import Deck from '../Deck/Deck';
import Pile, { PileHighlight } from '../Pile/Pile';
import PlayHistory from '../PlayHistory/PlayHistory';
import {
  getBurnChance,
  getPileRule,
  getSpecialCardSummary,
  IPileRule,
} from './feltRules';
import { FeltTexture } from './feltParts';

export const PLAY_AREA_ID = 'PLAY_ZONE';

interface IFeltMatProps {
  visibleGameState: IVisibleGameState;
  playHistory: IVisiblePlay[];
  pickup: () => void;
}

const RULE_ICON_PATHS: Record<IPileRule['direction'], string> = {
  up: 'M12 19V5M6 11l6-6 6 6',
  down: 'M12 5v14M6 13l6 6 6-6',
  any: 'M5 12h14M12 5v14',
};

function RulePill({ rule, summary }: { rule: IPileRule; summary: string }) {
  return (
    <div className="flex max-w-full items-center gap-[8px] rounded-full bg-accent-fill py-[6px] pr-[14px] pl-[8px] text-accent-on shadow-felt-chip">
      <span
        aria-hidden="true"
        className="flex size-[22px] shrink-0 items-center justify-center rounded-full bg-accent-on text-accent-fill"
      >
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d={RULE_ICON_PATHS[rule.direction]} />
        </svg>
      </span>
      <span className="shrink-0 text-[14px] font-bold">{rule.text}</span>
      {summary && (
        <span className="min-w-0 truncate text-[12px] font-medium text-accent-sub">
          · {summary}
        </span>
      )}
    </div>
  );
}

function FeltSlot({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-[8px]">
      {children}
      <span className="rounded-full bg-black/24 px-[9px] py-[3px] text-[12px] font-semibold">
        {label}
      </span>
    </div>
  );
}

export default function FeltMat({
  visibleGameState,
  playHistory,
  pickup,
}: IFeltMatProps) {
  const {
    deckCards,
    playedCards,
    burntCards,
    player,
    opponents,
    ruleSetOptions,
  } = visibleGameState;
  const { setNodeRef, active, isOver } = useDroppable({ id: PLAY_AREA_ID });

  const draggedCards: ISourcedCard[] = active?.data.current?.dragGroup ?? [];
  const isValidDrop =
    draggedCards.length > 0 &&
    playsContainingAll(
      player.availablePlays,
      draggedCards.map((c) => c.card.cardId)
    ).length > 0;
  const isPickup = player.isTurn && player.mustPickup;

  const pileHighlight = (): PileHighlight => {
    if (isPickup) {
      return 'danger';
    }
    if (isOver) {
      return isValidDrop ? 'drop' : 'danger';
    }
    return player.isTurn || isValidDrop ? 'turn' : 'none';
  };

  return (
    <section
      ref={setNodeRef}
      aria-label="Table"
      className="relative isolate z-10 mx-[12px] flex flex-col items-center gap-[14px] rounded-[24px] bg-felt px-[12px] pt-[16px] pb-[14px] text-felt-ink shadow-mat"
    >
      <FeltTexture />

      {ruleSetOptions && (
        <RulePill
          rule={getPileRule(playedCards, ruleSetOptions)}
          summary={getSpecialCardSummary(ruleSetOptions)}
        />
      )}

      <div className="flex items-center justify-center gap-[16px]">
        <FeltSlot label={`Deck ${deckCards.length}`}>
          <Deck deckCards={deckCards} />
        </FeltSlot>
        <FeltSlot label={`Pile ${playedCards.length}`}>
          <Pile
            playedCards={playedCards}
            highlight={pileHighlight()}
            burnChance={
              ruleSetOptions && getBurnChance(playedCards, ruleSetOptions)
            }
            onPickup={isPickup ? pickup : undefined}
          />
        </FeltSlot>
        <FeltSlot label={`Burnt ${burntCards.length}`}>
          <BurntCards burntCount={burntCards.length} />
        </FeltSlot>
      </div>

      <PlayHistory
        playHistory={playHistory}
        opponents={opponents}
        player={player}
      />
    </section>
  );
}
